import React, { useEffect, useState, useCallback } from "react";

import Modal from "shared/components/UIElements/Modal";
import Button from "shared/components/FormElements/Button";
import { useHttpClient } from "shared/hooks/http-hook";
import { getCurrentLanguage } from "shared/utility/lang";

const FIELD_LABELS_TR = {
  name: "Servis adı",
  plate: "Plaka",
  drivers: "Sürücü",
  driver: "Sürücü",
  routeClientArrivalTime: "Varış saati",
  starttime: "Başlangıç saati",
  stoptime: "Bitiş saati",
  usersdetails: "Yolcu sırası",
  routelocations: "Yolcu sırası",
  school: "Okul",
  isActive: "Aktif",
  isshift: "Vardiya servisi",
  day: "Gün",
  shift: "Vardiya",
  date: "Tarih",
  routeType: "Rota tipi",
  serviceType: "Servis tipi",
  serviceStatus: "Servis durumu",
  status: "Durum",
  useUsersDetails: "Detaylı yolcu saatleri",
  transferregion: "Transfer bölgesi",
  completionStatus: "Tamamlanma durumu",
};

const FIELD_LABELS_EN = {
  name: "Service name",
  plate: "Plate",
  drivers: "Driver",
  driver: "Driver",
  routeClientArrivalTime: "Arrival time",
  starttime: "Start time",
  stoptime: "Stop time",
  usersdetails: "Passenger order",
  routelocations: "Passenger order",
  school: "School",
  isActive: "Active",
  isshift: "Shift service",
  day: "Day",
  shift: "Shift",
  date: "Date",
  routeType: "Route type",
  serviceType: "Service type",
  serviceStatus: "Service status",
  status: "Status",
  useUsersDetails: "Passenger detail times",
  transferregion: "Transfer region",
  completionStatus: "Completion status",
};

// Servis değişikliği geçmişi modalı.
// Backend yolcu ve diğer alan değişikliklerini okunabilir metin olarak gönderir.
const ServiceAuditHistoryModal = ({ show, onClose, entityType, entityId }) => {
  const isTr = getCurrentLanguage() === "tr";
  const { sendRequest } = useHttpClient();

  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fieldLabels = isTr ? FIELD_LABELS_TR : FIELD_LABELS_EN;

  const labels = {
    title: isTr ? "Değişiklik Geçmişi" : "Change History",
    empty: isTr ? "Bu servis için kayıtlı bir değişiklik bulunamadı." : "No change history found for this service.",
    loading: isTr ? "Yükleniyor..." : "Loading...",
    close: isTr ? "Kapat" : "Close",
    by: isTr ? "Değişikliği yapan" : "Changed by",
    added: isTr ? "Eklenen yolcular" : "Added passengers",
    removed: isTr ? "Çıkarılan yolcular" : "Removed passengers",
    changes: isTr ? "Diğer değişiklikler" : "Other changes",
    unknownUser: isTr ? "Bilinmeyen kullanıcı" : "Unknown user",
    note30: isTr ? "Yalnızca son 30 günün kayıtları gösterilir." : "Only the last 30 days are shown.",
    impersonation: isTr ? "(temsilci oturumu)" : "(impersonation session)",
  };

  const actionLabel = (action) => {
    if (action === "create") return isTr ? "Oluşturuldu" : "Created";
    if (action === "delete") return isTr ? "Silindi" : "Deleted";
    if (action === "update") return isTr ? "Güncellendi" : "Updated";
    return action || "-";
  };

  const actionColor = (action) => {
    if (action === "create") return "#1b7f3b";
    if (action === "delete") return "#8a1c1c";
    return "#1f4e79";
  };

  const formatDate = (value) => {
    if (!value) return "-";
    try {
      const d = new Date(value);
      if (isNaN(d.getTime())) return "-";
      return d.toLocaleString(isTr ? "tr-TR" : "en-US");
    } catch (err) {
      return "-";
    }
  };

  const passengerNames = (list) => {
    if (!Array.isArray(list) || list.length === 0) return "";
    return list
      .map((p) => (p && (p.name || "").trim() ? p.name : isTr ? "Bilinmeyen yolcu" : "Unknown passenger"))
      .join(", ");
  };

  const fieldLabel = (field) => fieldLabels[field] || field || "-";

  const formatChangeValue = (field, value) => {
    if (!value || value === "-") return "-";
    if (field === "isActive" || field === "isshift" || field === "useUsersDetails") {
      if (value === "true") return isTr ? "Evet" : "Yes";
      if (value === "false") return isTr ? "Hayır" : "No";
    }
    return value;
  };

  const fetchLogs = useCallback(async () => {
    if (!show || !entityId || !entityType) {
      return;
    }
    setIsLoading(true);
    try {
      const res = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/service-audit?entityType=${encodeURIComponent(
          entityType,
        )}&entityId=${encodeURIComponent(entityId)}`,
      );
      setLogs(Array.isArray(res?.logs) ? res.logs : []);
    } catch (err) {
      setLogs([]);
    }
    setIsLoading(false);
  }, [show, entityId, entityType, sendRequest]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return (
    <Modal
      show={show}
      onCancel={onClose}
      onClose={onClose}
      header={labels.title}
      contentClass="audit-history-modal__content"
      footerClass="place-item__modal-actions"
      footer={
        <Button inverse onClick={onClose}>
          {labels.close}
        </Button>
      }
    >
      <div style={{ minWidth: "420px", maxWidth: "640px", maxHeight: "60vh", overflowY: "auto" }}>
        {/* <p style={{ fontSize: "12px", color: "#888", marginTop: 0 }}>{labels.note30}</p> */}

        {isLoading && <p>{labels.loading}</p>}

        {!isLoading && logs.length === 0 && <p>{labels.empty}</p>}

        {!isLoading &&
          logs.map((log, index) => {
            const added = passengerNames(log?.addedPassengers);
            const removed = passengerNames(log?.removedPassengers);
            const changes = Array.isArray(log?.changes) ? log.changes : [];
            return (
              <div
                key={log?._id || index}
                style={{
                  border: "1px solid #e5e5e5",
                  borderRadius: "6px",
                  padding: "10px 12px",
                  marginBottom: "10px",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
                  <span style={{ fontWeight: "bold", color: actionColor(log?.action) }}>
                    {actionLabel(log?.action)}
                  </span>
                  <span style={{ fontSize: "12px", color: "#666" }}>{formatDate(log?.createdAt)}</span>
                </div>

                <div style={{ marginTop: "4px", fontSize: "14px" }}>
                  <strong>{labels.by}:</strong> {log?.userName || labels.unknownUser}{" "}
                  {log?.impersonation ? (
                    <span style={{ color: "#b45309", fontSize: "12px" }}>{labels.impersonation}</span>
                  ) : null}
                </div>

                {removed ? (
                  <div style={{ marginTop: "4px", fontSize: "14px", color: "#8a1c1c" }}>
                    <strong>{labels.removed}:</strong> {removed}
                  </div>
                ) : null}

                {added ? (
                  <div style={{ marginTop: "4px", fontSize: "14px", color: "#1b7f3b" }}>
                    <strong>{labels.added}:</strong> {added}
                  </div>
                ) : null}

                {changes.length > 0 ? (
                  <div style={{ marginTop: "6px", fontSize: "14px" }}>
                    <strong>{labels.changes}:</strong>
                    <ul style={{ margin: "4px 0 0", paddingLeft: "18px" }}>
                      {changes.map((change, changeIndex) => (
                        <li key={`${change?.field || changeIndex}-${changeIndex}`} style={{ marginBottom: "4px" }}>
                          <strong>{fieldLabel(change?.field)}:</strong>{" "}
                          {formatChangeValue(change?.field, change?.before)} →{" "}
                          {formatChangeValue(change?.field, change?.after)}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            );
          })}
      </div>
    </Modal>
  );
};

export default ServiceAuditHistoryModal;
